import {
  Stethoscope,
  Droplets,
  Wrench,
  Snowflake,
  Settings,
  Zap,
  Car,
  ArrowLeft,
} from "lucide-react";
import { waLink } from "../lib/business";

const services = [
  {
    icon: Stethoscope,
    title: "فحص وتشخيص أعطال المكيف",
    desc: "نفحص أعطال التكييف بدقة عالية لتحديد السبب الحقيقي للمشكلة قبل البدء في أي إصلاح. لا تخمين، والنتيجة موثّقة.",
    wa: "أريد فحص وتشخيص عطل المكيف",
    big: true,
  },
  {
    icon: Droplets,
    title: "تعبئة فريون",
    desc: "تعبئة الفريون حسب الحاجة الفعلية للنظام، بعد التأكد من سلامة الدورة وعدم وجود تسريب.",
    wa: "أريد خدمة تعبئة فريون للسيارة",
  },
  {
    icon: Wrench,
    title: "ترهيم مكيفات السيارات",
    desc: "ترهيم وإصلاح مكيفات السيارات باحترافية، مع التركيز على جودة النتيجة وكفاءة التبريد.",
    wa: "أحتاج ترهيم مكيف سيارة",
  },
  {
    icon: Snowflake,
    title: "تغيير ثلاجة المكيف",
    desc: "تغيير ثلاجة (مبخّر) المكيف بقطعة مناسبة وفق موديل السيارة، مع التأكد من سلامة باقي الدورة.",
    wa: "أرغب في خدمة تغيير ثلاجة مكيف",
  },
  {
    icon: Settings,
    title: "إصلاح كمبروسر المكيف",
    desc: "إصلاح أو استبدال كمبروسر المكيف بعد فحص دقيق لحالته، مع ضمان كفاءة التشغيل.",
    wa: "أحتاج إصلاح كمبروسر المكيف",
  },
  {
    icon: Zap,
    title: "إصلاح كهرباء السيارات",
    desc: "تشخيص وإصلاح جميع أعطال كهرباء السيارات: وايرات، حساسات، شحن، إشارات، وأعطال الكمبيوتر.",
    wa: "أريد فحص كهرباء السيارة",
  },
  {
    icon: Car,
    title: "صيانة مكيف جميع السيارات",
    desc: "صيانة دورية وشاملة لمكيفات السيارات بجميع موديلاتها — يابانية، أمريكية، أوروبية، كورية — قديمة وحديثة.",
    wa: "صيانة مكيف سيارتي",
  },
];

export const Services = () => {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-20 lg:py-28 bg-slate-950"
    >
      {/* subtle top glow */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-500/40 to-transparent" />

      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12 lg:mb-16 reveal">
          <div>
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-cyan-400 font-semibold mb-4">
              <span className="w-8 h-px bg-cyan-400" />
              خدماتنا
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight max-w-3xl">
              كل ما يخصّ تكييف وكهرباء سيارتك — تحت سقف واحد
            </h2>
          </div>
          <p className="text-slate-400 max-w-md text-base lg:text-lg leading-relaxed">
            تخصص دقيق، معدّات تشخيص حديثة، وشغل بالأمانة — هذا ما يميّز خدماتنا
            عن الورش العامة.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {services.map((s, i) => {
            const Icon = s.icon;
            return (
              <a
                key={i}
                href={waLink(`السلام عليكم، ${s.wa}`)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`service-card-${i}`}
                className={`group relative rounded-2xl p-6 lg:p-7 border border-slate-800 bg-slate-900/60 hover:border-cyan-500/40 hover:bg-slate-900 transition-all duration-300 hover:-translate-y-1 ${
                  s.big ? "md:col-span-2 lg:col-span-1 lg:row-span-2" : ""
                }`}
              >
                {/* glow */}
                <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow-[0_0_40px_-8px_rgba(6,182,212,0.35)_inset]" />

                <div className="relative flex items-start justify-between">
                  <div className="w-12 h-12 lg:w-14 lg:h-14 rounded-xl bg-gradient-to-br from-cyan-500/20 to-cyan-500/5 border border-cyan-500/30 flex items-center justify-center">
                    <Icon className="w-6 h-6 lg:w-7 lg:h-7 text-cyan-400" />
                  </div>
                  <span className="text-[11px] font-semibold text-slate-500 tabular-nums">
                    0{i + 1}
                  </span>
                </div>

                <h3 className="relative mt-6 text-xl lg:text-2xl font-bold text-white leading-snug">
                  {s.title}
                </h3>
                <p className="relative mt-3 text-sm lg:text-base text-slate-400 leading-relaxed">
                  {s.desc}
                </p>

                <div className="relative mt-6 flex items-center gap-2 text-sm font-semibold text-cyan-400 group-hover:text-cyan-300">
                  احجز الخدمة عبر واتساب
                  <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                </div>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
